import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/SocialPlatformAdvanced.css';

interface User {
  id: string;
  name: string;
  avatar: string;
  verified: boolean;
  followers: number;
}

interface Post {
  id: string;
  author: User;
  content: string;
  images?: string[];
  likes: number;
  shares: number;
  comments: Comment[];
  timestamp: Date;
  liked: boolean;
  shared: boolean;
  engagement: number;
  trending: boolean;
}

interface Comment {
  id: string;
  author: User;
  content: string;
  timestamp: Date;
  likes: number;
}

interface Story {
  id: string;
  author: User;
  viewed: boolean;
}

const SocialPlatformAdvanced: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'feed' | 'explore' | 'stories'>('feed');
  const [onlineUsers, setOnlineUsers] = useState(1247);
  const [newPostContent, setNewPostContent] = useState('');
  const [notifications, setNotifications] = useState(8);

  useEffect(() => {
    setLoading(true);
    // Simulate API call with rich social data
    setTimeout(() => {
      const mockUsers: User[] = [
        { id: '1', name: 'Sarah Chen', avatar: '👩‍💼', verified: true, followers: 12540 },
        { id: '2', name: 'Alex Rivera', avatar: '👨‍🎨', verified: false, followers: 3200 },
        { id: '3', name: 'Dr. Maria Lopez', avatar: '👩‍⚕️', verified: true, followers: 8900 },
        { id: '4', name: 'Jake Wilson', avatar: '👨‍💻', verified: false, followers: 1450 },
        { id: '5', name: 'Emma Thompson', avatar: '👩‍🏫', verified: true, followers: 15600 }
      ];

      setPosts([
        {
          id: '1',
          author: mockUsers[0],
          content: 'Just launched our new AI-powered analytics dashboard! The real-time insights are incredible 🚀 #TechInnovation',
          images: ['📊', '🎯', '📈'],
          likes: 234,
          shares: 56,
          comments: [
            { id: 'c1', author: mockUsers[1], content: 'This looks amazing! Can\'t wait to try it out', timestamp: new Date(Date.now() - 3600000), likes: 12 },
            { id: 'c2', author: mockUsers[2], content: 'The healthcare applications for this are endless', timestamp: new Date(Date.now() - 7200000), likes: 8 }
          ],
          timestamp: new Date(Date.now() - 3600000),
          liked: false,
          shared: false,
          engagement: 89.5,
          trending: true
        },
        {
          id: '2',
          author: mockUsers[1],
          content: 'Working on some new digital art pieces. Here\'s a sneak peek of my latest project inspired by cyberpunk aesthetics ✨',
          images: ['🎨', '🌆', '💫'],
          likes: 187,
          shares: 34,
          comments: [
            { id: 'c3', author: mockUsers[0], content: 'Your style keeps getting better!', timestamp: new Date(Date.now() - 1800000), likes: 5 }
          ],
          timestamp: new Date(Date.now() - 5400000),
          liked: true,
          shared: false,
          engagement: 76.2,
          trending: false
        },
        {
          id: '3',
          author: mockUsers[2],
          content: 'Exciting breakthrough in our research lab today! We\'ve successfully developed a new diagnostic tool that could revolutionize early disease detection 🔬',
          likes: 456,
          shares: 89,
          comments: [
            { id: 'c4', author: mockUsers[3], content: 'This could save so many lives. Incredible work!', timestamp: new Date(Date.now() - 2700000), likes: 23 },
            { id: 'c5', author: mockUsers[4], content: 'As an educator, I\'d love to feature this in our science curriculum', timestamp: new Date(Date.now() - 3600000), likes: 18 }
          ],
          timestamp: new Date(Date.now() - 7200000),
          liked: false,
          shared: true,
          engagement: 94.1,
          trending: true
        }
      ]);

      setStories(mockUsers.map((user, index) => ({
        id: `story-${user.id}`,
        author: user,
        viewed: index < 2
      })));

      setLoading(false);
    }, 1500);
  }, [activeTab]);

  // Real-time updates simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setOnlineUsers(prev => prev + Math.floor(Math.random() * 10) - 5);
      
      if (Math.random() > 0.7) {
        setNotifications(prev => prev + 1);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleLike = (postId: string) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          liked: !post.liked,
          likes: post.liked ? post.likes - 1 : post.likes + 1
        };
      }
      return post;
    }));
  };

  const handleShare = (postId: string) => {
    setPosts(posts.map(post => {
      if (post.id === postId) {
        return {
          ...post,
          shared: !post.shared,
          shares: post.shared ? post.shares - 1 : post.shares + 1
        };
      }
      return post;
    }));
  };

  const handlePost = () => {
    if (!newPostContent.trim()) return;
    
    const newPost: Post = {
      id: Date.now().toString(),
      author: { id: 'me', name: 'You', avatar: '🙂', verified: false, followers: 0 },
      content: newPostContent,
      likes: 0,
      shares: 0,
      comments: [],
      timestamp: new Date(),
      liked: false,
      shared: false,
      engagement: 0,
      trending: false
    };
    
    setPosts([newPost, ...posts]);
    setNewPostContent('');
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="social-loading">
        <div className="loading-spinner"></div>
        <p>Loading social feed...</p>
      </div>
    );
  }

  return (
    <div className="social-platform-advanced">
      <div className="social-header">
        <div className="header-left">
          <Link to="/" className="back-button-social">← Back to Home</Link>
          <div className="title-section">
            <h1>🌐 Social Command Center</h1>
            <div className="version-badge-social v15">Synoppy 1.5</div>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="live-stats">
            <div className="online-indicator">
              <div className="pulse-dot"></div>
              <span>{onlineUsers.toLocaleString()} online</span>
            </div>
            <div className="notifications">
              <span className="notification-icon">🔔</span>
              {notifications > 0 && <span className="notification-badge">{notifications}</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="social-nav">
        {(['feed', 'explore', 'stories'] as const).map(tab => (
          <button
            key={tab}
            className={activeTab === tab ? 'active' : ''}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'feed' && '🏠 Feed'}
            {tab === 'explore' && '🔍 Explore'}
            {tab === 'stories' && '📸 Stories'}
          </button>
        ))}
      </div>

      {activeTab === 'stories' && (
        <div className="stories-section">
          <div className="stories-container">
            {stories.map(story => (
              <div 
                key={story.id} 
                className={`story-item ${story.viewed ? 'viewed' : ''}`}
              >
                <div className="story-avatar">{story.author.avatar}</div>
                <div className="story-name">{story.author.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab !== 'stories' && (
        <>
          <div className="post-composer">
            <div className="composer-header">
              <div className="user-avatar">🙂</div>
              <textarea
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                placeholder="What's happening?"
                className="post-input"
              />
            </div>
            <div className="composer-actions">
              <div className="media-buttons">
                <button className="media-btn">📷 Photo</button>
                <button className="media-btn">🎥 Video</button>
                <button className="media-btn">📍 Location</button>
              </div>
              <button 
                className="post-button"
                onClick={handlePost}
                disabled={!newPostContent.trim()}
              >
                Post
              </button>
            </div>
          </div>

          <div className="posts-feed">
            {posts.map(post => (
              <div key={post.id} className="post-card">
                <div className="post-header">
                  <div className="author-info">
                    <div className="author-avatar">{post.author.avatar}</div>
                    <div className="author-details">
                      <div className="author-name">
                        {post.author.name}
                        {post.author.verified && <span className="verified">✓</span>}
                      </div>
                      <div className="post-meta">
                        <span>{formatTime(post.timestamp)}</span>
                        <span className="followers">{post.author.followers.toLocaleString()} followers</span>
                        {post.trending && <span className="trending">🔥 Trending</span>}
                      </div>
                    </div>
                  </div>
                  <div className="post-menu">⋯</div>
                </div>

                <div className="post-content">
                  {post.content}
                  {post.images && (
                    <div className="post-images">
                      {post.images.map((img, index) => (
                        <div key={index} className="post-image">{img}</div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="post-stats">
                  <div className="engagement-meter">
                    <div className="meter-bar">
                      <div 
                        className="meter-fill"
                        style={{ width: `${post.engagement}%` }}
                      ></div>
                    </div>
                    <span className="engagement-text">{post.engagement}% engagement</span>
                  </div>
                </div>

                <div className="post-actions">
                  <button 
                    className={`action-btn like-btn ${post.liked ? 'active' : ''}`}
                    onClick={() => handleLike(post.id)}
                  >
                    {post.liked ? '❤️' : '🤍'} {post.likes}
                  </button>
                  <button className="action-btn comment-btn">
                    💬 {post.comments.length}
                  </button>
                  <button 
                    className={`action-btn share-btn ${post.shared ? 'active' : ''}`}
                    onClick={() => handleShare(post.id)}
                  >
                    🔄 {post.shares}
                  </button>
                  <button className="action-btn bookmark-btn">
                    🔖 Save
                  </button>
                </div>

                {post.comments.length > 0 && (
                  <div className="comments-section">
                    {post.comments.slice(0, 2).map(comment => (
                      <div key={comment.id} className="comment">
                        <div className="comment-avatar">{comment.author.avatar}</div>
                        <div className="comment-content">
                          <div className="comment-header">
                            <span className="comment-author">{comment.author.name}</span>
                            <span className="comment-time">{formatTime(comment.timestamp)}</span>
                          </div>
                          <p>{comment.content}</p>
                          <div className="comment-actions">
                            <button className="comment-like">👍 {comment.likes}</button>
                            <button className="comment-reply">Reply</button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {post.comments.length > 2 && (
                      <button className="view-more-comments">
                        View {post.comments.length - 2} more comments
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <div className="version-comparison-social">
        <Link to="?version=1.0" className="downgrade-link-social">
          Compare with Synoppy 1.0 Version →
        </Link>
      </div>

      <div className="features-showcase">
        <div className="feature">📱 Real-time messaging</div>
        <div className="feature">📊 Advanced analytics</div>
        <div className="feature">🎯 Smart content discovery</div>
        <div className="feature">🔒 Advanced privacy controls</div>
        <div className="feature">🤖 AI content moderation</div>
        <div className="feature">📺 Live streaming</div>
      </div>
    </div>
  );
};

export default SocialPlatformAdvanced;